import React, { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    TextField,
    CircularProgress,
    Box,
    Menu,
    MenuItem,
    Grid,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { styled, makeStyles } from "@mui/styles";
import DatePickerControl from "../helpers/DatePickerControl";
import {
    Add as AddIcon,
    DeleteForever as DeleteIcon
} from "@mui/icons-material";
import { setSnackAction } from "../redux/actions/snackActions";
import caseEmployeeService from "../services/caseEmployeeService";
import { setCaseEmployeesAction } from "../redux/actions/caseActions";
import useProtectedCharacteristics from "../hooks/queries/useProtectedCharacteristics";

const IconButton = styled(Button)({
    padding: '2px',
    marginTop: '-2px',
    marginLeft: '2px',
    minWidth: 0,
    color: grey[700],
});

const useStyles = makeStyles(theme => ({
    textField: {
        marginTop: theme.spacing(3)
    }
}));

const emptyEmployee = {
    employeeId: null,
    employeeNumber: null,
    name: "",
    position: "",
    employmentStartDate: null,
    protectedCharacteristics: [],
    manager: {},
    isStartEstimate: false,
    isLeaver: false,
    dateOfBirth: null,
    notes: "",
};

const EmployeeFormDialog = ({
    open,
    existingEmployee,
    handleFormClose,
    setExcludedEmployeeIds,
    excludedEmployeeIds
}) => {
    const classes = useStyles();

    const [employee, setEmployee] = useState(existingEmployee || JSON.parse(JSON.stringify(emptyEmployee)));
    const [saving, setSaving] = useState(false);

    const { currentCaseId: caseId, cases } = useSelector(state => state.caseReducer);
    const employees = cases[caseId].employees;
    const accountId = cases[caseId].account.accountId;

    const dispatch = useDispatch();

    const [anchorEl, setAnchorEl] = useState();    

    const { data: protectedCharacteristics } = useProtectedCharacteristics();

    const protectedCharacteristicName = useCallback((id) => protectedCharacteristics?.find(x => x.key === id)?.value, [protectedCharacteristics]);

    const addProtectedCharacteristic = (id) => {
        setAnchorEl();
        setEmployee({
            ...employee,
            protectedCharacteristics: [...employee.protectedCharacteristics, { protectedCharacteristicId: id }]
        });
    }

    const removeProtectedCharacteristic = (index) => {        
        setEmployee({
            ...employee,
            protectedCharacteristics: employee.protectedCharacteristics.filter((pc, i) => i !== index)
        });
    }

    const handleChangeEmployee = e => {
        setEmployee({
            ...employee,
            [e.target.name]: e.target.value
        });
    };

    const setInfo = (index, value) => {
        setEmployee({
            ...employee,
            protectedCharacteristics: [...employee.protectedCharacteristics.map((x, i) => index === i ? ({ ...x, info: value }) : x)]
        });
    }

    const handleCancel = () => {
        setEmployee(JSON.parse(JSON.stringify(emptyEmployee)));
        handleFormClose();
    }

    const handleSubmit = async e => {
        e.preventDefault();

        let newEmployeeId = null;
        if (!existingEmployee) {
            try {
                setSaving(true);
                const { newEmployee, hasMatch } = await caseEmployeeService.addNewEmployee({ caseId, accountId, employee });
                
                newEmployeeId = newEmployee.employeeId;
                dispatch(setCaseEmployeesAction(caseId, [...employees, newEmployee]));

                if (hasMatch)
                    dispatch(setSnackAction("Saved! We've found a possible match between the employee and an existing contact. Please check if the case visibility should be limited to contacts on the case.", "warning"));
                else
                    dispatch(setSnackAction("Saved!", "success"));

                handleCancel();
            }
            catch (error) {
                dispatch(setSnackAction(error?.message || "Could not add employee to case", "error"))
            }
            finally {
                setSaving(false);
                setExcludedEmployeeIds([...excludedEmployeeIds, newEmployeeId]);
            }
        } else {
            try {
                setSaving(true);
                await caseEmployeeService.editCaseEmployee({ employee, caseId });
                dispatch(setCaseEmployeesAction(caseId, employees.map(e => e.employeeId === employee.employeeId ?
                    {
                        ...e,
                        ...employee
                    } : e)));
                dispatch(setSnackAction("Saved!", "success"));
                handleCancel();
            }
            catch (error) {
                dispatch(setSnackAction(error?.message || "Could not edit case employee", "error"))
            }
            finally {
                setSaving(false);
            }
        }
    };
  
    return (
        <Dialog open={open} onClose={handleFormClose} maxWidth="sm" fullWidth>
            <DialogTitle>{existingEmployee ? "Edit" : "Add"} Employee</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <DialogContentText>Please enter employee details</DialogContentText>
                    <FormGroup>
                        <>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        className={classes.textField}
                                        name="firstName"
                                        required
                                        size="small"
                                        value={employee.firstName}
                                        onChange={handleChangeEmployee}
                                        inputProps={{ maxLength: 100 }}
                                        label="Employee First Name"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        className={classes.textField}
                                        name="lastName"
                                        required
                                        size="small"
                                        value={employee.lastName}
                                        onChange={handleChangeEmployee}
                                        inputProps={{ maxLength: 100 }}
                                        label="Employee Last Name"
                                    />
                                </Grid>                                
                            </Grid>
                            <TextField
                                className={classes.textField}
                                name="employeeNumber"
                                value={employee.employeeNumber || " "}
                                size="small"
                                onChange={handleChangeEmployee}
                                inputProps={{ maxLength: 80 }}
                                label="Employee Number"
                            />
                            <TextField
                                className={classes.textField}
                                name="position"
                                size="small"
                                value={employee.position}
                                onChange={handleChangeEmployee}
                                label="Employee Position"
                            />                            
                            <DatePickerControl
                                className={classes.textField}
                                size="small"
                                label="Employment Start Date"
                                value={employee?.employmentStartDate ? new Date(employee.employmentStartDate) : null}
                                onChange={date => setEmployee({ ...employee, employmentStartDate: date })}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={employee.isStartEstimate}
                                        onChange={e => setEmployee({ ...employee, isStartEstimate: e.target.checked })}
                                    />
                                }
                                label="Start date is an estimate"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={employee.isLeaver}
                                        onChange={e => setEmployee({ ...employee, isLeaver: e.target.checked })}
                                    />
                                }
                                label="Employee is a leaver"
                            />
                            <DatePickerControl
                                name="dateOfBirth"
                                size="small"
                                label="Employee Date of Birth"
                                value={employee?.dateOfBirth ? new Date(employee.dateOfBirth) : null}
                                onChange={date => setEmployee({ ...employee, dateOfBirth: date })}
                            />
                            <TextField
                                fullWidth
                                className={classes.textField}
                                size="small"
                                name="notes"
                                value={employee.notes}
                                inputProps={{ maxLength: 255 }}
                                multiline
                                onChange={handleChangeEmployee}
                                label="General employee notes"
                            />
                            <Box className={classes.textField} display="flex" justifyContent="space-between">
                                <DialogContentText >Protected characteristics</DialogContentText>
                                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}><AddIcon /></IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={() => setAnchorEl(null)}
                                >
                                    {protectedCharacteristics?.filter(pc => !(employee.protectedCharacteristics.map(epc => epc.protectedCharacteristicId).includes(pc.key)))
                                        .map(pc => <MenuItem key={pc.key} onClick={() => addProtectedCharacteristic(pc.key)}>{pc.value}</MenuItem>)}
                                </Menu>
                            </Box>
                            {employee.protectedCharacteristics.map((pc, index) =>
                                <div key={index}>
                                    <div style={{fontSize: '0.8em', marginTop: '5px'}}>{protectedCharacteristicName(pc.protectedCharacteristicId)}</div>
                                    <Box display="flex">
                                        <TextField
                                            flex={1}
                                            fullWidth
                                            placeholder="Optional Info"
                                            size="small"
                                            value={pc.info || ""}
                                            onChange={(e) => setInfo(index, e.target.value)}
                                        />
                                        <IconButton onClick={() => removeProtectedCharacteristic(index)}><DeleteIcon /></IconButton>
                                    </Box>
                                </div>
                            )}
                        </>                        
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    {saving ? <CircularProgress /> :
                        <>
                            <Button onClick={handleCancel}>Cancel</Button>
                            <Button variant="contained" color="primary" type="submit" disabled={!employee.firstName?.length || !employee.lastName?.length}>{existingEmployee ? "Done" : "Submit"}</Button>
                        </>}
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default EmployeeFormDialog;